/**
 * Pestaña Analítica: donut de distribución de gastos, ocupación mensual y 4 métricas.
 */
import { useQuery } from '@tanstack/react-query';
import { api, queryKeys } from '../../api/client';
import { Card, CardSectionLabel } from '../../components/Card';
import { KpiStrip, MetricCard } from '../../components/KpiCard';
import { BarChart } from '../../components/BarChart';
import { DonutChart } from '../../components/DonutChart';
import { ErrorState } from '../../components/EmptyState';
import { SkeletonCard, SkeletonKpiRow } from '../../components/Skeleton';
import { formatEur, formatEurPrecise, formatNumber, formatPercent } from '../../lib/format';
import styles from '../DashboardPage.module.css';

export function AnalyticsTab() {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: queryKeys.dashboardAnalytics,
    queryFn: api.dashboardAnalytics,
  });

  if (isPending) {
    return (
      <>
        <div className={styles.analyticsGrid}>
          <SkeletonCard rows={1} height={230} />
          <div className={styles.analyticsOccupancy}>
            <SkeletonCard rows={1} height={150} />
          </div>
        </div>
        <SkeletonKpiRow count={4} />
      </>
    );
  }

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const { metrics } = data;

  return (
    <>
      <div className={styles.analyticsGrid}>
        <Card className={styles.analyticsDonutCard}>
          <CardSectionLabel>Distribución de gastos</CardSectionLabel>
          <DonutChart gradient={data.expenseDonutGradient} slices={data.expenseDistribution} />
        </Card>

        <Card className={styles.analyticsOccupancy}>
          <CardSectionLabel>Ocupación por mes</CardSectionLabel>
          <BarChart
            data={data.occupancyTrend.map((point) => ({
              key: point.month,
              label: point.label,
              heightPct: point.heightPct,
              title: `${point.label}: ${formatPercent(point.occupancyRate)} · ${
                point.occupiedUnits
              } de ${point.totalUnits} habitaciones`,
            }))}
          />
        </Card>
      </div>

      <KpiStrip compact>
        <MetricCard label="Ingreso medio / m²" value={formatEurPrecise(metrics.incomePerSqm)} />
        <MetricCard label="Días vacío / año" value={formatNumber(metrics.vacantDaysPerYear)} />
        <MetricCard
          label="Coste medio incidencia"
          value={metrics.averageIssueCost === null ? '—' : formatEur(metrics.averageIssueCost)}
        />
        <MetricCard
          label="Ratio gastos / ingresos"
          value={formatPercent(metrics.expenseToIncomeRatio, 0)}
        />
      </KpiStrip>
    </>
  );
}
